<div class="data-table-container">
    <!-- Global Filter -->
    <div *ngIf="config.globalFilter" class="mb-3 flex-shrink-0">
        <span class="p-input-icon-left">
            <i class="pi pi-search"></i>
            <input type="text" (input)="onGlobalFilter($event)" placeholder="Search..."
                class="p-inputtext-sm w-full px-3 py-2 border border-gray-300 rounded"
                [(ngModel)]="globalFilterValue" />
        </span>
    </div>

    <!-- Table Container -->
    <div class="table-wrapper">
        <!-- Table -->
        <div class="table-scroll-container" [class.scrollbar-thin]="showScrollBar" infiniteScroll [infiniteScrollThrottle]="300" (scrolled)="onScrolled()"
            [infiniteScrollDistance]="1" [scrollWindow]="false">
            <table [class]="'simple-table ' + tableClass">
                <!-- Header -->
                <thead class="table-header">
                    <tr>
                        <!-- Selection Column -->
                        <th *ngIf="config.selectable" class="selection-column cursor-pointer"
                            (click)="onSelectionHeaderCellClick($event)">
                            <input type="checkbox" [checked]="isAllSelected()" [indeterminate]="isSomeSelected()"
                                (change)="toggleAllSelection($event)" />
                        </th>

                        <!-- Data Columns -->
                        <th *ngFor="let col of visibleColumns" [style.width]="col.width" [class]="col.headerClass"
                            [class.sortable-column]="isColumnSortable(col)"
                            (click)="isColumnSortable(col) ? handleColumnSort(col) : null">
                            <div class="header-content">
                                <ng-container *ngIf="col.editable && editingHeaderField === col.field; else staticHeader">
                                    <div class="header-edit-wrapper"
                                        (keydown.enter)="saveHeaderEdit(col)"
                                        (keydown.escape)="cancelHeaderEdit()"
                                        (click)="$event.stopPropagation()">
                                        <app-input
                                            [(ngModel)]="editingHeaderValue"
                                            [size]="'sm'"
                                            (blur)="saveHeaderEdit(col)" />
                                    </div>
                                </ng-container>
                                <ng-template #staticHeader>
                                    <span class="group flex items-center gap-1 cursor-pointer"
                                        (click)="col.editable ? startHeaderEdit(col, $event) : null">
                                        {{ col.editable ? col.header : (col.header | textCase) }}
                                        <app-icon *ngIf="col.editable && col.icon"
                                            [type]="col.icon"
                                            [source]="col.iconSource ?? 'lucide'"
                                            [size]="'xs'"
                                            class="opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </span>
                                    <i *ngIf="isColumnSortable(col)" [class]="getSortIcon(col)" class="sort-icon"></i>
                                </ng-template>
                            </div>
                        </th>
                    </tr>
                </thead>

                <!-- Body -->
                <tbody class="table-body">
                    <!-- Loading or Data Content -->
                    <ng-container *ngIf="config.loading; else dataContent">
                        <ng-container *ngTemplateOutlet="loadingTemplate"></ng-container>
                    </ng-container>

                    <!-- Loading Template -->
                    <ng-template #loadingTemplate>
                        @for(i of getSkeletonCount(); track $index) {
                        <tr>
                            <!-- Selection Column Skeleton -->
                            <td *ngIf="config.selectable" class="selection-column">
                                <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                            </td>
                            <!-- Data Columns Skeletons -->
                            <td *ngFor="let col of visibleColumns" [class]="col.cssClass">
                                <p-skeleton width="100%" height="1.2rem"></p-skeleton>
                            </td>
                        </tr>
                        }
                    </ng-template>

                    <!-- Data Content Template -->
                    <ng-template #dataContent>
                        <!-- Data Rows -->
                        <tr *ngFor="let rowData of paginatedData; let rowIndex = index"
                            [class]="getRowClass(rowData, rowIndex)" (click)="onRowClick(rowData, $event)">

                            <!-- Selection Column -->
                            <td *ngIf="config.selectable" class="selection-column cursor-pointer"
                                (click)="onSelectionCellClick(rowData, $event)">
                                <input type="checkbox" [checked]="isRowSelected(rowData)"
                                    [disabled]="!isRowSelectable(rowData)"
                                    (change)="toggleRowSelection(rowData, $event)" />
                            </td>

                            <!-- Data Columns -->
                            <td *ngFor="let col of visibleColumns" [class]="col.cssClass">
                                <!-- Custom Template -->
                                <ng-container *ngIf="col.type === 'custom' && col.template">
                                    <ng-container
                                        *ngTemplateOutlet="getTemplate(col.template, col); context: { $implicit: rowData, field: col.field, column: col, index: rowIndex }">
                                    </ng-container>
                                </ng-container>

                                <!-- Text -->
                                <span *ngIf="col.type === 'text' || !col.type">
                                    {{ getFieldValue(rowData, col.field) }}
                                </span>

                                <!-- Date -->
                                <span *ngIf="col.type === 'date'">
                                    {{ getFieldValue(rowData, col.field) | date:(col.pipeArgs?.[0] || 'dd/MM/yyyy') }}
                                </span>

                                <!-- Number -->
                                <span *ngIf="col.type === 'number'">
                                    {{ getFieldValue(rowData, col.field) | number:(col.pipeArgs?.[0] || '1.0-2') }}
                                </span>

                                <!-- Boolean -->
                                <i *ngIf="col.type === 'boolean'"
                                    [class]="getFieldValue(rowData, col.field) ? 'pi pi-check text-success' : 'pi pi-times text-danger'">
                                </i>

                                <!-- Badge -->
                                <span *ngIf="col.type === 'badge'"
                                    [class]="'inline-block px-2 py-1 text-xs rounded-full ' + getBadgeClass(getFieldValue(rowData, col.field))">
                                    {{ getFieldValue(rowData, col.field) }}
                                </span>
                            </td>
                        </tr>

                        <!-- Empty State -->
                        <tr *ngIf="filteredData.length === 0">
                            <td [attr.colspan]="totalColumns" class="empty-state h-full">
                                <div
                                    class="text-gray-500 text-center py-8 h-full items-center justify-center flex flex-col">
                                    <ng-container *ngIf="customEmptyTemplate; else defaultEmpty">
                                        <ng-container *ngTemplateOutlet="customEmptyTemplate"></ng-container>
                                    </ng-container>
                                    <ng-template #defaultEmpty>
                                        <i class="pi pi-info-circle text-4xl mb-2 block"></i>
                                        <p>{{ config.emptyMessage || 'No data found' | textCase }}</p>
                                    </ng-template>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                </tbody>
            </table>
        </div>

        <!-- PrimeNG Pagination -->
        <div class="flex-shrink-0" *ngIf="config.paginator && totalRecords > 0">
            <p-paginator [rows]="config.rows" [totalRecords]="totalRecords"
                [rowsPerPageOptions]="config.rowsPerPageOptions" [first]="currentFirst"
                (onPageChange)="onPageChange($event)" class="custom-paginator" appendTo="body">
            </p-paginator>
        </div>
    </div>
</div>